"use client"

import { useCallback } from "react"
import { Pencil } from "lucide-react"
import { useTranslations } from "next-intl"
import { Button } from "@/components/ui/button"
import { useToast } from "@/hooks/use-toast"
import { editListingEvents } from "@/lib/edit-listing-events"
import { cn } from "@/lib/utils"

type ListingOwnerEditButtonProps = {
  listingId: string
  title: string
  price: string | number
  currency?: string | null
  images?: string[]
  description?: string
  condition?: string
  listingFormat?: string | null
  variant?: "product-page" | "product-footer" | "header-icon" | "header-icon-overlay"
  className?: string
}

export function ListingOwnerEditButton({
  listingId,
  title,
  price,
  currency,
  images = [],
  description = "",
  condition = "",
  listingFormat = null,
  variant = "product-page",
  className,
}: ListingOwnerEditButtonProps) {
  const tATC = useTranslations("addToCart")
  const tML = useTranslations("myListings")
  const { toast } = useToast()

  const openEdit = useCallback(() => {
    if (listingFormat === "auction") {
      toast({
        title: tML("auctionNoEdit"),
        description: tML("auctionNoEditDesc"),
        variant: "destructive",
      })
      return
    }
    editListingEvents.open({
      listingId,
      listing: {
        id: listingId,
        title,
        price,
        currency,
        image_urls: images,
        description,
        condition,
        listing_format: listingFormat,
      },
      messageId: "",
      roomName: "",
    })
  }, [listingId, title, price, currency, images, description, condition, listingFormat, tML, toast])

  if (variant === "product-footer") {
    return (
      <Button
        type="button"
        className={cn(
          "h-11 w-full rounded-full bg-muted text-foreground hover:bg-muted/80 font-semibold",
          className,
        )}
        size="lg"
        onClick={openEdit}
      >
        <Pencil className="h-4 w-4 shrink-0" />
        {tATC("edit")}
      </Button>
    )
  }

  if (variant === "header-icon-overlay") {
    return (
      <button
        type="button"
        aria-label={tATC("edit")}
        className={cn(
          "inline-flex h-11 w-11 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-md",
          className,
        )}
        onClick={openEdit}
      >
        <Pencil className="h-5 w-5" />
      </button>
    )
  }

  if (variant === "header-icon") {
    return (
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={tATC("edit")}
        className={cn("rounded-full h-10 w-10 shrink-0 hover:bg-muted", className)}
        onClick={openEdit}
      >
        <Pencil className="h-5 w-5" />
      </Button>
    )
  }

  return (
    <Button
      type="button"
      className={cn(
        "h-[50px] w-full rounded-full mb-4 flex items-center justify-center gap-2 font-medium",
        "bg-black/5 text-foreground border border-border/50 hover:bg-black/10",
        "dark:bg-white/10 dark:border-white/20 dark:hover:bg-white/15",
        className,
      )}
      onClick={openEdit}
    >
      <Pencil className="h-5 w-5 shrink-0" />
      {tATC("edit")}
    </Button>
  )
}
